import { useState } from "react";
import { cn } from "../lib/utils";

export interface SpeedDialAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface SpeedDialProps {
  actions: SpeedDialAction[];
  icon?: React.ReactNode;
  closeIcon?: React.ReactNode;
  direction?: "up" | "down" | "left" | "right";
  openOnHover?: boolean;
  className?: string;
}

const defaultIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const closeIconDefault = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export function SpeedDial({
  actions,
  icon = defaultIcon,
  closeIcon = closeIconDefault,
  direction = "up",
  openOnHover = false,
  className,
}: SpeedDialProps) {
  const [open, setOpen] = useState(false);

  const dirClass = `sd-dir-${direction}`;

  function handleActionClick(action: SpeedDialAction) {
    if (action.disabled) return;
    action.onClick();
    setOpen(false);
  }

  return (
    <div
      className={cn("sd-root", dirClass, className)}
      onMouseEnter={openOnHover ? () => setOpen(true) : undefined}
      onMouseLeave={openOnHover ? () => setOpen(false) : undefined}
    >
      <div className={cn("sd-actions", open && "sd-actions-open")}>
        {actions.map((action, i) => (
          <div
            key={action.id}
            className="sd-action-item"
            style={{ "--sd-index": i } as React.CSSProperties}
          >
            <span className="sd-action-label">{action.label}</span>
            <button
              className={cn("sd-action-btn", action.disabled && "sd-action-btn-disabled")}
              disabled={action.disabled}
              onClick={() => handleActionClick(action)}
              aria-label={action.label}
              title={action.label}
            >
              {action.icon}
            </button>
          </div>
        ))}
      </div>

      <button
        className={cn("sd-trigger", open && "sd-trigger-open")}
        aria-label={open ? "Close actions" : "Open actions"}
        aria-expanded={open}
        onClick={() => !openOnHover && setOpen(v => !v)}
      >
        <span className={cn("sd-trigger-icon", !open && "sd-trigger-icon-visible")}>{icon}</span>
        <span className={cn("sd-trigger-icon", open && "sd-trigger-icon-visible")}>{closeIcon}</span>
      </button>
    </div>
  );
}
